import { useCallback, useState } from 'react';

/**
 * Flow-state manager for the V1 -> V2 restaking wizard.
 *
 * Steps (3):
 *   0 UNSTAKE   - signature 1: unstake(fullBalance) on the V1 pool
 *   1 WITHDRAW  - pick the V2 pool + withdraw(V1) + stake into V2
 *   2 COMPLETE  - success screen
 *
 * Resume is purely on-chain: the detail page reads `inUnStakeAmount` and drops
 * the user on the Withdraw step when there is a pending unstaked balance. No
 * localStorage is used - the rare "already withdrawn to wallet but not staked"
 * interruption is intentionally NOT auto-resumed (tokens are safe in the wallet).
 */

export const RESTAKE_STEPS = {
  UNSTAKE: 0,
  WITHDRAW: 1,
  COMPLETE: 2,
} as const;

export type RestakeStep = (typeof RESTAKE_STEPS)[keyof typeof RESTAKE_STEPS];

export type RestakeTxStatus =
  | 'idle'
  | 'signing1' // waiting for the user to sign the V1 unstake
  | 'pending1' // V1 unstake tx mining
  | 'signing2' // waiting for the user to sign withdraw / approve / V2 stake
  | 'pending2' // V2 stake tx mining
  | 'done'
  | 'error';

interface UseRestakeFlowArgs {
  v1Pool?: string;
  address?: string;
  initialStep?: number;
}

export const useRestakeFlow = ({
  initialStep = RESTAKE_STEPS.UNSTAKE,
}: UseRestakeFlowArgs) => {
  const [flowStep, setFlowStep] = useState<number>(initialStep);
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null);
  // restakeAmount is kept as a human-readable string (token units, not wei).
  const [restakeAmount, setRestakeAmount] = useState<string>('');
  const [txStatus, setTxStatus] = useState<RestakeTxStatus>('idle');

  const goNext = useCallback(
    () => setFlowStep((s) => Math.min(s + 1, RESTAKE_STEPS.COMPLETE)),
    []
  );
  const goBack = useCallback(
    () => setFlowStep((s) => Math.max(s - 1, RESTAKE_STEPS.UNSTAKE)),
    []
  );
  const reset = useCallback(() => {
    setFlowStep(RESTAKE_STEPS.UNSTAKE);
    setSelectedPoolId(null);
    setRestakeAmount('');
    setTxStatus('idle');
  }, []);

  return {
    flowStep,
    setFlowStep,
    selectedPoolId,
    setSelectedPoolId,
    restakeAmount,
    setRestakeAmount,
    txStatus,
    setTxStatus,
    goNext,
    goBack,
    reset,
  };
};
